import React, { useState, useEffect } from 'react';
import { Filter, MapPin, Search } from 'lucide-react';
import { fairApi } from '../services/api';
import { Location } from '../utils/geolocation';

interface FilterProps {
  selectedState: string;
  selectedCity: string;
  searchName: string;
  selectedAttendantStatus: string;
  radiusMiles: number | null;
  maxAdmissionPrice: number | null;
  selectedCategory: string;
  onStateChange: (state: string) => void;
  onCityChange: (city: string) => void;
  onSearchNameChange: (searchName: string) => void;
  onAttendantStatusChange: (status: string) => void;
  onRadiusChange: (radius: number | null) => void;
  onMaxAdmissionChange: (price: number | null) => void;
  onCategoryChange: (category: string) => void;
  userLocation?: Location;
  onLocationRequest?: () => void;
}

interface FilterOptions {
  states: string[];
  cities: string[];
}

export default function FairFilters({
  selectedState,
  selectedCity,
  searchName,
  selectedAttendantStatus,
  radiusMiles,
  maxAdmissionPrice,
  selectedCategory,
  onStateChange,
  onCityChange,
  onSearchNameChange,
  onAttendantStatusChange,
  onRadiusChange,
  onMaxAdmissionChange,
  onCategoryChange,
  userLocation,
  onLocationRequest
}: FilterProps) {
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    states: [],
    cities: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setLoading(true);
        const fairs = await fairApi.getAll();
        
        // Extract unique states
        const states = Array.from(new Set(fairs.map(stand => stand.address?.state).filter(Boolean))).sort();

        // Extract unique cities
        const cities = Array.from(new Set(fairs.map(stand => stand.address?.city).filter(Boolean))).sort();

        setFilterOptions({ states, cities });
      } catch (error: unknown) {
        console.error('Failed to load filter options:', error);
        // Set empty arrays as fallback
        setFilterOptions({ states: [], cities: [] });
      } finally {
        setLoading(false);
      }
    };

    loadFilterOptions();
  }, []);

  const { states, cities } = filterOptions;

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-3 space-y-4">
        <div className="flex items-center space-x-2 mb-2">
          <Filter className="h-4 w-4 text-primary-dark" />
          <h2 className="text-base font-semibold text-primary-dark">Filters</h2>
        </div>
        <div className="space-y-4">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </div>
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </div>
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-3 space-y-4">
      <div className="flex items-center space-x-2 mb-2">
        <Filter className="h-4 w-4 text-primary-dark" />
        <h2 className="text-base font-semibold text-primary-dark">Filters</h2>
      </div>

      {/* Location Button */}
      {onLocationRequest && (
        <div className="mb-4">
          <button
            onClick={onLocationRequest}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors w-full ${
              userLocation
                ? 'bg-teal-50 text-primary-dark border-teal-200'
                : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
            }`}
          >
            <MapPin className="h-4 w-4" />
            <span>{userLocation ? 'Location enabled' : 'Find nearby farms'}</span>
          </button>
        </div>
      )}

      {/* Search Filter */}
      <div>
        <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
          Search by Name
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            id="search"
            type="text"
            title="Search artist fairs by name"
            value={searchName}
            onChange={(e) => onSearchNameChange(e.target.value)}
            placeholder="Search artist fairs..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary text-sm"
            data-testid="fair-search-input"
          />
        </div>
      </div>

      {/* State Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          State
        </label>
        <select
          value={selectedState}
          onChange={(e) => onStateChange(e.target.value)}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
          disabled={states.length === 0}
          data-testid="fair-state-select"
        >
          <option value="">All States</option>
          {states.map(state => (
            <option key={state} value={state}>{state}</option>
          ))}
        </select>
      </div>

      {/* City Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          City
        </label>
        <select
          value={selectedCity}
          onChange={(e) => onCityChange(e.target.value)}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
          disabled={cities.length === 0}
          data-testid="fair-city-select"
        >
          <option value="">All Cities</option>
          {cities.map(city => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
      </div>

      {/* Attendant Status Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Type
        </label>
        <select
          value={selectedAttendantStatus}
          onChange={(e) => onAttendantStatusChange(e.target.value)}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
          data-testid="fair-attendantStatus-select"
        >
          <option value="">All Types</option>
          <option value="attended">Attended</option>
          <option value="self-serve">Self-Serve</option>
        </select>
      </div>

      {/* Phase 2: Radius Search Filter (Issue #1197) */}
      {/* Only rendered if user location is available */}
      {userLocation && (
        <div>
          <label htmlFor="radius-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Distance
          </label>
          <select
            id="radius-filter"
            value={radiusMiles ?? ''}
            onChange={(e) => onRadiusChange(e.target.value ? Number(e.target.value) : null)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
            data-testid="radius-filter"
          >
            <option value="">All distances</option>
            <option value="5">Within 5 miles</option>
            <option value="10">Within 10 miles</option>
            <option value="25">Within 25 miles</option>
            <option value="50">Within 50 miles</option>
            <option value="100">Within 100 miles</option>
          </select>
        </div>
      )}

      {/* Phase 2: Admission Price Filter (Issue #1197) */}
      <div>
        <label htmlFor="admission-price-filter" className="block text-sm font-medium text-gray-700 mb-1">
          Admission Price
        </label>
        <select
          id="admission-price-filter"
          value={maxAdmissionPrice ?? ''}
          onChange={(e) => onMaxAdmissionChange(e.target.value ? Number(e.target.value) : null)}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
          data-testid="admission-price-filter"
        >
          <option value="">Any price</option>
          <option value="0">Free only</option>
          <option value="5">Under $5</option>
          <option value="10">Under $10</option>
          <option value="20">Under $20</option>
        </select>
      </div>

      {/* Phase 2: Fair Category Filter (Issue #1197) */}
      <div>
        <label htmlFor="category-filter" className="block text-sm font-medium text-gray-700 mb-1">
          Category
        </label>
        <select
          id="category-filter"
          value={selectedCategory}
          onChange={(e) => onCategoryChange(e.target.value)}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm"
          data-testid="category-filter"
        >
          <option value="">All Types</option>
          <option value="Craft Fair">Craft Fair</option>
          <option value="Farmers Market">Farmers Market</option>
          <option value="Art Show">Art Show</option>
          <option value="Street Festival">Street Festival</option>
          <option value="Trade Show">Trade Show</option>
          <option value="Music Festival">Music Festival</option>
          <option value="Food Festival">Food Festival</option>
          <option value="Holiday Market">Holiday Market</option>
          <option value="Artisan Market">Artisan Market</option>
          <option value="Community Fair">Community Fair</option>
        </select>
      </div>

      {/* Active Filters */}
      {(selectedState || selectedCity || selectedAttendantStatus || radiusMiles || maxAdmissionPrice !== null || selectedCategory) && (
        <div className="border-t pt-2">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xs font-medium text-gray-700">Active Filters:</h3>
            <button
              onClick={() => {
                onStateChange('');
                onCityChange('');
                onSearchNameChange('');
                onAttendantStatusChange('');
                onRadiusChange(null);
                onMaxAdmissionChange(null);
                onCategoryChange('');
              }}
              className="text-xs text-secondary hover:text-primary-dark font-medium transition-colors"
              data-testid="clear-all-fair-filters-button"
            >
              Clear All
            </button>
          </div>
          <div className="flex flex-wrap gap-1">
            {selectedState && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-primary-dark">
                {selectedState}
              </span>
            )}
            {selectedCity && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-primary-dark">
                {selectedCity}
              </span>
            )}
            {selectedAttendantStatus && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-moonbone text-ink capitalize">
                {selectedAttendantStatus}
              </span>
            )}
            {radiusMiles && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Within {radiusMiles} miles
              </span>
            )}
            {maxAdmissionPrice !== null && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {maxAdmissionPrice === 0 ? 'Free only' : `Under $${maxAdmissionPrice}`}
              </span>
            )}
            {selectedCategory && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                {selectedCategory}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}